"use client";

import React, { useMemo, memo } from "react";
import { ShellLink } from "@/src/components";
import { useParams } from "next/navigation";
import { cn, formatTitleForUrl } from "utils";
import { AssetImage, Icon, ospThemes } from "@ospweb/sdk-ui";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import type { MarketSummaryItemProps, MarketSummaryAsset } from "./types";
import Translations from "@translate/components/Home/MarketSummary/MarketSummaryItem";
import { I18nUtil } from "@utils/i18n";
import { formatBigNumber, numberStatus } from "@/src/utils";
import { ROUTER_PATH } from "@/src/config/router";
import { buildLocale } from "@/src/utils/buildLocale";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 资产信息内容组件
 * 使用 memo 优化，避免不必要的重新渲染导致的图片闪烁
 */
const AssetContent = memo<{
  asset?: MarketSummaryAsset;
}>(({ asset }) => {
  // 使用 useMemo 稳定 icon URL，避免引用变化导致重新加载
  // 必须在所有条件语句之前调用 hooks
  const iconSrc = useMemo(() => asset?.icon, [asset?.icon]);

  if (!asset) {
    return <div className="flex-1" />;
  }

  return (
    <>
      {/* 资产图标 */}
      <div
        className={cn(
          "border border-border_solid rounded-full shrink-0 overflow-hidden",
          "size-5 md:mr-1",
        )}
      >
        {iconSrc ? (
          <AssetImage
            key={iconSrc}
            src={iconSrc}
            alt={asset.symbol}
            width={20}
            height={20}
            className="w-full h-full object-cover rounded-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-background_solid_secondary">
            <span
              className={cn(
                "text-content_tertiary font-medium",
                "text-xs md:text-[6px]",
              )}
            >
              {asset.symbol?.charAt(0) || "?"}
            </span>
          </div>
        )}
      </div>

      {/* 资产符号 */}
      <div
        className={cn(
          "flex items-center not-italic shrink-0",
          "md:mr-1 md:gap-1",
        )}
      >
        <p
          className={cn(
            "text-content_primary",
            "body_medium_m md:body_medium_s",
          )}
        >
          {asset.name}
        </p>
      </div>

      {/* 涨跌幅 */}
      {asset.change !== undefined &&
        (() => {
          const { isNull, absValue } = numberStatus(asset.change);
          const percentage = isNull
            ? "--"
            : `${formatBigNumber(absValue, {
                needFormat: true,
                trimZeros: true,
                precision: 2,
              })}%`;

          return (
            <div className="flex gap-0.5 items-center">
              <div className="relative shrink-0">
                <Icon
                  name={asset.change >= 0 ? "upvote" : "downvote"}
                  size={12}
                  className={
                    asset.change >= 0
                      ? "text-content_accent1_primary"
                      : "text-content_accent2_primary"
                  }
                />
              </div>
              <p
                className={cn(
                  "body_medium_s text-center",
                  asset.change >= 0
                    ? "text-content_accent1_primary"
                    : "text-content_accent2_primary",
                )}
              >
                {percentage}
              </p>
            </div>
          );
        })()}
    </>
  );
});

AssetContent.displayName = "AssetContent";

/**
 * MarketSummaryItem 组件
 * 单个市场摘要卡片，支持 PC 和 H5 两种显示模式
 */
export const MarketSummaryItem: React.FC<MarketSummaryItemProps> = memo(
  ({
    item,
    isExpanded = false,
    onToggle,
    isFirst = false,
    isLast = false,
    className,
    onAskClick,
  }) => {
    const { title, description, asset, sourceLink, category } = item;
    const params = useParams();
    const locale = (params?.locale as string) || "en";

    // 构建资产详情页链接
    const assetDetailUrl = useMemo(() => {
      if (!asset || (!asset.id && !asset.symbol)) {
        return null;
      }

      if (asset.type === "crypto" && asset.id) {
        return `${ROUTER_PATH.CRYPTO_TOKEN}/${asset.id}`;
      } else if (asset.type === "stock" && asset.symbol) {
        return `${ROUTER_PATH.STOCK_DETAIL}/${asset.symbol}`;
      }

      return null;
    }, [asset]);

    // 构建新闻详情页链接
    const newsDetailUrl = useMemo(() => {
      if (!sourceLink || !category) {
        return null;
      }
      const urlPrefix = buildLocale(locale);
      const formattedSourceLink = formatTitleForUrl(sourceLink);
      return `${urlPrefix}${ROUTER_PATH.NEWS_TITLE_DETAIL}/${formattedSourceLink}`;
    }, [sourceLink, category, locale]);

    // 处理 Ask 按钮点击
    const handleAskClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onAskClick?.(item);
    };

    // 处理展开/收起按钮点击
    const handleToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onToggle?.();
    };

    const cardClassName = cn(
      "flex flex-col gap-3 w-full",
      "md:items-start md:shrink-0 md:p-4",
      "md:transition-colors md:duration-200",
      // 根据位置应用圆角（仅 PC 端）
      isFirst && "md:rounded-t-4",
      isLast && "md:rounded-b-4",
      newsDetailUrl && "md:hover:bg-background_white_overlay5 cursor-pointer",
      className,
    );

    const cardContent = (
      <>
        {/* 内容区域 */}
        <div className="flex flex-col gap-1 w-full md:items-start">
          {/* 标题行：移动端单独显示，PC 端与 Ask 按钮同一行 */}
          <div className="flex items-center justify-between w-full gap-2 md:gap-3">
            <p
              className={cn(
                "body_medium_l text-content_primary w-full whitespace-pre-wrap",
                "md:flex-1 md:truncate md:body_bold_l md:overflow-ellipsis md:overflow-hidden md:shrink-0",
              )}
            >
              {title}
            </p>
            {/* PC 端：Ask 按钮放在标题后面 */}
            {onAskClick && (
              <button
                aria-label="Market Summary Item"
                data-openreplay-label="Market Summary Item Button"
                id="btn-market-summary-item"
                className={cn(
                  "button px-2 py-[6px] rounded-[6px] text-content_primary flex items-center gap-1 shrink-0",
                  "md:xs_button",
                  "hidden md:flex",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAskClick(e);
                }}
              >
                <Icon
                  name="p_chat"
                  size={16}
                  color={ospThemes.colors.content_primary}
                />
                <span className="button_s text-content_primary">
                  {I18n.t("Ask")}
                </span>
              </button>
            )}
          </div>

          {/* 描述文本 */}
          {/* 移动端：仅在展开时显示 */}
          {/* PC 端：始终显示，可 line-clamp-2 */}
          <p
            className={cn(
              "body_m text-content_secondary w-full whitespace-pre-wrap",
              // 移动端：条件显示
              !isExpanded && "hidden",
              // PC 端：始终显示，可 line-clamp-2
              "md:block md:flex-1 md:line-clamp-2",
            )}
          >
            {description}
          </p>
        </div>

        {/* 底部信息栏 */}
        <div
          className={cn(
            "flex items-center justify-between w-full gap-2 md:gap-0",
            {
              "md:hidden": !assetDetailUrl,
            },
          )}
        >
          {/* 左侧：资产信息或占位符 */}
          {assetDetailUrl ? (
            <ShellLink
              aria-label="Market Summary Item"
              data-openreplay-label={`Market Summary Item Link | ${assetDetailUrl}`}
              id="lnk-market-summary-item"
              href={assetDetailUrl}
              className={cn(
                "flex gap-1 items-center cursor-pointer",
                "no-underline text-current",
                "md:gap-0",
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <AssetContent asset={asset} />
            </ShellLink>
          ) : (
            <div className="flex gap-1 items-center md:gap-0">
              <AssetContent asset={asset} />
            </div>
          )}

          {/* 右侧：移动端显示 Ask 按钮和展开/收起按钮，PC 端隐藏 */}
          <div className="flex items-center gap-1 md:hidden">
            {onAskClick && (
              <>
                <button
                  aria-label="Market Summary Item"
                  data-openreplay-label="Market Summary Item Button"
                  id="btn-market-summary-item-2"
                  className={cn(
                    "button px-2 py-[6px] rounded-[6px] text-content_primary flex items-center gap-1",
                    "hover:bg-background_white_overlay5",
                  )}
                  onClick={handleAskClick}
                >
                  <Icon
                    name="p_chat"
                    size={16}
                    color={ospThemes.colors.content_primary}
                  />
                  <span className="button_s text-content_primary">
                    {I18n.t("Ask")}
                  </span>
                </button>
                {/* 竖线分隔线 */}
                <div className="w-px h-4 bg-border_solid shrink-0" />
              </>
            )}
            <button
              aria-label="Market Summary Item"
              data-openreplay-label="Market Summary Item Button"
              id="btn-market-summary-item-3"
              onClick={handleToggle}
              className="box-border flex items-center px-[2px] justify-center rounded-[6px] shrink-0"
            >
              <OspIcons
                name={isExpanded ? "chevron-up" : "chevron-down"}
                size={16}
                className="text-content_primary"
              />
            </button>
          </div>
        </div>
      </>
    );

    // 如果有新闻链接，使用 Link 包装；否则直接返回 div
    if (newsDetailUrl) {
      return (
        <ShellLink
          aria-label="Market Summary Item"
          data-openreplay-label={`Market Summary Item Link | ${newsDetailUrl}`}
          id="lnk-market-summary-item-2"
          href={newsDetailUrl}
          className={cn(cardClassName, "no-underline text-current")}
        >
          {cardContent}
        </ShellLink>
      );
    }

    return <div className={cardClassName}>{cardContent}</div>;
  },
  (prevProps, nextProps) => {
    // 自定义比较函数，只在关键 props 变化时才重新渲染
    // 这可以避免不必要的重新渲染导致的图片闪烁
    return (
      prevProps.item.id === nextProps.item.id &&
      prevProps.item.title === nextProps.item.title &&
      prevProps.item.description === nextProps.item.description &&
      prevProps.item.asset?.icon === nextProps.item.asset?.icon &&
      prevProps.item.asset?.symbol === nextProps.item.asset?.symbol &&
      prevProps.item.asset?.change === nextProps.item.asset?.change &&
      prevProps.item.asset?.id === nextProps.item.asset?.id &&
      prevProps.item.asset?.type === nextProps.item.asset?.type &&
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.isFirst === nextProps.isFirst &&
      prevProps.isLast === nextProps.isLast &&
      prevProps.className === nextProps.className
    );
  },
);

MarketSummaryItem.displayName = "MarketSummaryItem";
